import { DownloadOutlined } from '@ant-design/icons';
import { Button, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useDealerPlacements, useDealerReceipts, useDealerReturns } from '../../api/hooks';
import { date, money } from '../../utils/format';

interface ReceiptRow {
  key: string;
  date: string;
  type: 'Investment' | 'Withdrawal' | 'Profit Payment';
  reference: string;
  amount: number;
  url: string;
  fileName: string;
}

const TYPE_COLOR: Record<ReceiptRow['type'], string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

/** Every transaction PDF for this dealer in one place — mirrors the investor Receipts tab exactly. */
export default function DealerReceiptsTab({ dealerId }: { dealerId: number }) {
  const { data: placements, isLoading: loadingPlacements } = useDealerPlacements(dealerId);
  const { data: returns, isLoading: loadingReturns } = useDealerReturns(dealerId);
  const { data: receipts, isLoading: loadingReceipts } = useDealerReceipts(dealerId);

  const rows: ReceiptRow[] = [
    ...(placements ?? []).map((p) => ({
      key: `inv-${p.id}`,
      date: p.placementDate,
      type: 'Investment' as const,
      reference: `INV-R-${String(p.id).padStart(6, '0')}`,
      amount: p.amount,
      url: `/receipts/dealer-investment/${p.id}`,
      fileName: `investment_receipt_${p.id}.pdf`,
    })),
    ...(returns ?? []).map((r) => ({
      key: `wd-${r.id}`,
      date: r.returnDate,
      type: 'Withdrawal' as const,
      reference: `WD-R-${String(r.id).padStart(6, '0')}`,
      amount: r.amount,
      url: `/receipts/dealer-withdrawal/${r.id}`,
      fileName: `withdrawal_receipt_${r.id}.pdf`,
    })),
    ...(receipts ?? []).map((r) => ({
      key: `pp-${r.id}`,
      date: r.receiptDate,
      type: 'Profit Payment' as const,
      reference: `PP-R-${String(r.id).padStart(6, '0')}`,
      amount: r.amount,
      url: `/receipts/dealer-profit-payment/${r.id}`,
      fileName: `profit_payment_receipt_${r.id}.pdf`,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Table<ReceiptRow>
      rowKey="key"
      loading={loadingPlacements || loadingReturns || loadingReceipts}
      dataSource={rows}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: 'Date', dataIndex: 'date', render: date },
        {
          title: 'Type',
          dataIndex: 'type',
          render: (value: ReceiptRow['type']) => <Tag color={TYPE_COLOR[value]}>{value}</Tag>,
        },
        { title: 'Receipt no', dataIndex: 'reference' },
        { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
        {
          title: 'Download',
          render: (_, row) => (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadFile(row.url, row.fileName).catch((err) => message.error(errorMessage(err)))}
            >
              PDF
            </Button>
          ),
        },
      ]}
    />
  );
}
